import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  getOrganizationDocuments,
  deleteDocument,
  renameDocument,
  removeTagFromDocument,
  shareDocument,
} from "../../../utils/Api";
import { useNavigate } from "react-router-dom";
import DeleteDocumentModal from "../../../modals/DeleteDocumentModal";
import AddTagsToDocumentsModal from "../../../modals/AddTagsToDocumentsModal";
import ShareDocumentModal from "../../../modals/ShareDocumentModal";
import NoAccessModal from "../../../modals/NoAccessModal";
import LoadingPage from "../../../components/LoadingPage";
import { useDocumentContext } from "../../../contexts/DocumentContext";
import { useAuth } from "../../../utils/AuthContext";

import {
  Pencil,
  Trash2,
  ArrowUp,
  ArrowDown,
  Tag as TagIcon,
  FileText,
  Eye,
  MoreVertical,
  Lock,
} from "lucide-react";

import SecondaryButton from "../../../components/Buttons/SecondaryButton";
import Tag from "../../../components/Tag";
import { Member } from "./Members";

type DocumentItem = Record<string, any>;

const PAGE_SIZE = 20;

interface DocumentsProps {
  organizationId: string;
  userRole: string;
  members: Member[];
}

const Documents: React.FC<DocumentsProps> = ({
  organizationId,
  userRole,
  members,
}) => {
  const { refreshSidebar } = useDocumentContext();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [documentList, setDocumentList] = useState<DocumentItem[]>([]);
  const [totalDocuments, setTotalDocuments] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isAddTagsModalOpen, setIsAddTagsModalOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState<DocumentItem | null>(
    null,
  );
  const [isDeleting, setIsDeleting] = useState(false);
  const [editingDocId, setEditingDocId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState<string>("");
  const [selectedDocIds, setSelectedDocIds] = useState<Set<string>>(new Set());
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [debouncedSearch, setDebouncedSearch] = useState<string>("");
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [documentToShare, setDocumentToShare] = useState<DocumentItem | null>(
    null,
  );
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [isNoAccessModalOpen, setIsNoAccessModalOpen] = useState(false);
  const [noAccessDocument, setNoAccessDocument] = useState<DocumentItem | null>(
    null,
  );
  const [autoSendAccessRequest, setAutoSendAccessRequest] = useState(false);
  const [menuPosition, setMenuPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);

  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const remainingCount = totalDocuments - documentList.length;

  const fetchDocuments = useCallback(
    async (offset: number, append: boolean, limitOverride?: number) => {
      try {
        if (offset === 0) setLoading(true);
        else setLoadingMore(true);

        const params: Parameters<typeof getOrganizationDocuments>[1] = {
          limit: limitOverride ?? PAGE_SIZE,
          offset,
          sort_order: sortOrder,
        };
        if (debouncedSearch.trim()) params.search = debouncedSearch.trim();

        const { data } = await getOrganizationDocuments(organizationId, params);

        let docs: DocumentItem[] = [];
        if (data.documents && typeof data.documents === "object") {
          docs = Object.entries(data.documents).map(
            ([id, docData]: [string, any]) => ({
              ...docData,
              id,
            }),
          );
        }

        if (append) {
          setDocumentList((prev) => [...prev, ...docs]);
        } else {
          setDocumentList(docs);
        }
        setTotalDocuments(data.total_documents ?? 0);
        setError(null);
      } catch (err: any) {
        setError(err.message ?? "Error fetching documents");
        if (!append) setDocumentList([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [organizationId, debouncedSearch, sortOrder],
  );

  useEffect(() => {
    fetchDocuments(0, false);
  }, [fetchDocuments]);

  useEffect(() => {
    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
      }
    };
  }, []);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(value);
    }, 300);
  };

  const toggleSortOrder = () => {
    setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
  };

  const handleShowMore = () => {
    fetchDocuments(documentList.length, true);
  };

  const refetch = () => {
    const currentCount = Math.max(documentList.length, PAGE_SIZE);
    fetchDocuments(0, false, currentCount);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (openMenuId && !target.closest(".menu-container")) {
        setOpenMenuId(null);
        setMenuPosition(null);
      }
    };

    const handleScroll = () => {
      if (openMenuId) {
        setOpenMenuId(null);
        setMenuPosition(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleScroll, true);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [openMenuId]);

  const handleEditClick = (doc: DocumentItem, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingDocId(doc.id);
    setEditingName(doc.name);
  };

  const handleSaveEdit = async (docId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await renameDocument(docId, editingName);
      setEditingDocId(null);
      setEditingName("");
      refetch();
    } catch (err: any) {
      setError(err.message ?? "Error renaming document");
    }
  };

  const handleCancelEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingDocId(null);
    setEditingName("");
  };

  const handleDeleteClick = (doc: DocumentItem, e: React.MouseEvent) => {
    e.stopPropagation();
    setDocumentToDelete(doc);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!documentToDelete) return;

    setIsDeleting(true);
    try {
      await deleteDocument(documentToDelete.id);
      setIsDeleteModalOpen(false);
      setDocumentToDelete(null);
      refetch();
    } catch (err: any) {
      setError(err.message ?? "Error deleting document");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = new Set(documentList.map((doc) => doc.id));
      setSelectedDocIds(allIds);
    } else {
      setSelectedDocIds(new Set());
    }
  };

  const handleSelectDocument = (docId: string, checked: boolean) => {
    const newSelected = new Set(selectedDocIds);
    if (checked) {
      newSelected.add(docId);
    } else {
      newSelected.delete(docId);
    }
    setSelectedDocIds(newSelected);
  };

  const handleDeleteSelected = async () => {
    if (selectedDocIds.size === 0) return;

    setIsDeleting(true);
    try {
      await Promise.all(
        Array.from(selectedDocIds).map((id) => deleteDocument(id)),
      );
      setSelectedDocIds(new Set());
      refetch();
    } catch (err: any) {
      setError(err.message ?? "Error deleting documents");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleRemoveTag = async (tagId: string, documentId: string) => {
    try {
      await removeTagFromDocument(tagId, documentId);
      refetch();
      refreshSidebar();
    } catch (err: any) {
      setError(err.message ?? "Error removing tag from document");
    }
  };

  const handleShareClick = (doc: DocumentItem, e: React.MouseEvent) => {
    e.stopPropagation();
    setDocumentToShare(doc);
    setIsShareModalOpen(true);
  };

  const handleShare = async (userIds: string[]) => {
    if (!documentToShare?.id) return;
    await shareDocument(documentToShare.id, userIds);
    refetch();
  };

  const allDisplayedSelected =
    documentList.length > 0 &&
    documentList.every((doc) => selectedDocIds.has(doc.id));

  const isAdmin = userRole.toLowerCase() === "admin";

  const currentUserMember = members.find((m) => m.email === user?.email);

  const isDocumentOwner = (doc: DocumentItem) => {
    return currentUserMember?.user_id === doc.user_id;
  };

  const getOwnerName = (doc: DocumentItem) => {
    const member = members.find((m) => m.user_id === doc.user_id);
    if (member) {
      return member.name || member.email;
    }
    return "Former member";
  };

  const getOwnerEmail = (doc: DocumentItem) => {
    const member = members.find((m) => m.user_id === doc.user_id);
    return member?.email;
  };

  const hasAccess = (doc: DocumentItem) => {
    if (isAdmin || isDocumentOwner(doc)) return true;
    return doc.has_access !== false;
  };

  const handleDocumentClick = (doc: DocumentItem) => {
    if (hasAccess(doc)) {
      navigate(`/view/${doc.id}`);
    } else {
      setAutoSendAccessRequest(false);
      setNoAccessDocument(doc);
      setIsNoAccessModalOpen(true);
    }
  };

  return (
    <>
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h6 className="text-base font-semibold">Documents</h6>
          {selectedDocIds.size > 0 && isAdmin && (
            <div className="flex rounded-full overflow-hidden border border-black bg-white h-10">
              <button
                onClick={() => setIsAddTagsModalOpen(true)}
                disabled={isDeleting}
                className="flex items-center justify-center px-4 py-2 border-r border-r-black"
              >
                <TagIcon size={18} />
              </button>

              <button
                onClick={handleDeleteSelected}
                disabled={isDeleting}
                className="flex items-center justify-center px-4 py-2"
              >
                <Trash2 size={18} />
              </button>
            </div>
          )}
        </div>

        <div className="mb-4">
          <input
            type="text"
            className="w-full sm:max-w-md px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#1d365d] focus:border-transparent"
            placeholder="Search by name..."
            aria-label="Search by name"
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
        </div>

        {error && (
          <div className="mb-4">
            <div
              className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded"
              role="alert"
            >
              {error}
            </div>
          </div>
        )}

        {loading ? (
          <LoadingPage
            fullScreen={false}
            variant="light"
            message="Loading documents"
          />
        ) : (
          <div className="bg-white rounded shadow overflow-x-auto">
            <table className="w-full">
              <thead className="bg-white border-b border-gray-200">
                <tr>
                  {isAdmin && (
                    <th className="px-3 sm:px-6 py-3 text-left text-gray-600 w-12">
                      <input
                        type="checkbox"
                        checked={allDisplayedSelected}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        className="w-4 h-4 cursor-pointer"
                        aria-label="Select all documents"
                        disabled={documentList.length === 0}
                      />
                    </th>
                  )}
                  <th className="px-3 sm:px-6 py-3 text-left text-gray-600">
                    Name
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-gray-600 hidden md:table-cell">
                    Labels
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-gray-600 hidden lg:table-cell">
                    Owner
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-gray-600 hidden sm:table-cell">
                    Pages
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-gray-600">
                    <button
                      onClick={toggleSortOrder}
                      className="flex items-center gap-2 text-gray-600 transition-colors"
                      title={`Sort by date ${
                        sortOrder === "asc" ? "descending" : "ascending"
                      }`}
                      disabled={documentList.length === 0}
                    >
                      Created at
                      {sortOrder === "desc" ? (
                        <ArrowDown size={16} />
                      ) : (
                        <ArrowUp size={16} />
                      )}
                    </button>
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-right text-gray-600 w-16 sm:w-auto">
                    <span className="hidden sm:inline">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {documentList.length === 0 ? (
                  <tr>
                    <td
                      colSpan={isAdmin ? 8 : 7}
                      className="px-3 sm:px-6 py-12 text-center"
                    >
                      <div className="text-gray-500">
                        <FileText className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                        {searchQuery ? (
                          <>
                            <h4 className="text-lg font-semibold mb-2">
                              No documents found
                            </h4>
                            <p>
                              No documents match your search "{searchQuery}"
                            </p>
                          </>
                        ) : (
                          <>
                            <h4 className="text-lg font-semibold mb-2">
                              No documents yet
                            </h4>
                            <p>
                              Upload documents to this organisation to get
                              started
                            </p>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ) : (
                  documentList.map((doc) => (
                    <tr
                      key={doc.id}
                      className="hover:bg-gray-50 transition-colors duration-150"
                    >
                      {isAdmin && (
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={selectedDocIds.has(doc.id)}
                            onChange={(e) =>
                              handleSelectDocument(doc.id, e.target.checked)
                            }
                            onClick={(e) => e.stopPropagation()}
                            className="w-4 h-4 cursor-pointer"
                            aria-label={`Select ${doc.name || "document"}`}
                          />
                        </td>
                      )}
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                        {editingDocId === doc.id ? (
                          <input
                            type="text"
                            value={editingName}
                            onChange={(e) => setEditingName(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            className="px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-[#1d365d] focus:border-transparent w-full max-w-[150px] sm:max-w-none"
                            autoFocus
                          />
                        ) : (
                          <span
                            className={`flex items-center gap-2 cursor-pointer ${
                              hasAccess(doc)
                                ? "text-[#1d365d] hover:text-[#2d4a75]"
                                : "text-gray-400"
                            }`}
                            onClick={() => handleDocumentClick(doc)}
                          >
                            {!hasAccess(doc) && (
                              <Lock
                                size={14}
                                className="text-gray-400 flex-shrink-0"
                              />
                            )}
                            <span className="truncate max-w-[150px] sm:max-w-none">
                              {doc.name || "Untitled Document"}
                            </span>
                          </span>
                        )}
                      </td>
                      <td className="px-3 sm:px-6 py-4 hidden md:table-cell">
                        {hasAccess(doc) ? (
                          <div className="flex flex-wrap gap-1">
                            {doc.tags && doc.tags.length > 0 ? (
                              doc.tags.map((tag: any) =>
                                isAdmin || isDocumentOwner(doc) ? (
                                  <Tag
                                    key={tag.id}
                                    id={tag.id}
                                    name={tag.name}
                                    color={tag.color}
                                    onRemove={(tagId) =>
                                      handleRemoveTag(tagId, doc.id)
                                    }
                                  />
                                ) : (
                                  <Tag
                                    key={tag.id}
                                    id={tag.id}
                                    name={tag.name}
                                    color={tag.color}
                                  />
                                ),
                              )
                            ) : (
                              <span className="text-gray-400 text-sm">
                                Uncategorised
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400">--</span>
                        )}
                      </td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-gray-700 hidden lg:table-cell">
                        {getOwnerName(doc)}
                      </td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-gray-700 hidden sm:table-cell">
                        {hasAccess(doc) ? doc.page_count || 0 : "--"}
                      </td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-gray-700 text-sm">
                        {doc.created_at
                          ? new Date(doc.created_at).toLocaleString("en-GB", {
                              day: "numeric",
                              month: "short",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: false,
                            })
                          : "Recently uploaded"}
                      </td>
                      <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm">
                        {editingDocId === doc.id ? (
                          <div className="flex gap-2 justify-end">
                            <button
                              onClick={(e) => handleSaveEdit(doc.id, e)}
                              className="text-green-600 hover:text-green-800 font-medium"
                            >
                              Save
                            </button>
                            <button
                              onClick={handleCancelEdit}
                              className="text-gray-600 hover:text-gray-800 font-medium"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <div className="flex justify-end">
                            {(isDocumentOwner(doc) ||
                              isAdmin ||
                              !hasAccess(doc)) && (
                              <div className="relative menu-container">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (openMenuId === doc.id) {
                                      setOpenMenuId(null);
                                      setMenuPosition(null);
                                    } else {
                                      const rect =
                                        e.currentTarget.getBoundingClientRect();
                                      setMenuPosition({
                                        top: rect.bottom + 4,
                                        left: rect.right - 192,
                                      });
                                      setOpenMenuId(doc.id);
                                    }
                                  }}
                                  className="p-1 hover:bg-gray-200 rounded transition-colors"
                                  type="button"
                                >
                                  <MoreVertical className="w-5 h-5 text-gray-600" />
                                </button>
                                {openMenuId === doc.id && menuPosition && (
                                  <div
                                    className="fixed w-48 bg-white rounded-lg shadow-xl z-50 border border-gray-200 overflow-hidden"
                                    style={{
                                      top: menuPosition.top,
                                      left: menuPosition.left,
                                      boxShadow:
                                        "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
                                    }}
                                  >
                                    <div className="py-1">
                                      {hasAccess(doc) ? (
                                        <>
                                          <button
                                            onClick={(e) => {
                                              handleShareClick(doc, e);
                                              setOpenMenuId(null);
                                            }}
                                            className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2"
                                            type="button"
                                          >
                                            <Eye size={16} />
                                            <span>Edit access</span>
                                          </button>
                                          <button
                                            onClick={(e) => {
                                              handleEditClick(doc, e);
                                              setOpenMenuId(null);
                                            }}
                                            className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2"
                                            type="button"
                                          >
                                            <Pencil size={16} />
                                            <span>Rename</span>
                                          </button>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setSelectedDocIds(
                                                new Set([doc.id]),
                                              );
                                              setIsAddTagsModalOpen(true);
                                              setOpenMenuId(null);
                                            }}
                                            className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2"
                                            type="button"
                                          >
                                            <TagIcon size={16} />
                                            <span>Add label</span>
                                          </button>
                                          <div className="border-t border-gray-100"></div>
                                          <button
                                            onClick={(e) => {
                                              handleDeleteClick(doc, e);
                                              setOpenMenuId(null);
                                            }}
                                            className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors flex items-center gap-2"
                                            type="button"
                                          >
                                            <Trash2 size={16} />
                                            <span>Delete</span>
                                          </button>
                                        </>
                                      ) : (
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setAutoSendAccessRequest(true);
                                            setNoAccessDocument(doc);
                                            setIsNoAccessModalOpen(true);
                                            setOpenMenuId(null);
                                          }}
                                          className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2"
                                          type="button"
                                        >
                                          <Lock size={16} />
                                          <span>Request access</span>
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* pagination controls */}
        {!loading && documentList.length > 0 && (
          <div className="mt-6 text-center">
            {remainingCount > 0 && (
              <SecondaryButton
                onClick={handleShowMore}
                className="mb-3"
                disabled={loadingMore}
              >
                {loadingMore
                  ? "Loading..."
                  : `Show ${Math.min(remainingCount, PAGE_SIZE)} more documents`}
              </SecondaryButton>
            )}
            <p className="text-gray-600 text-center">
              Showing {documentList.length} out of {totalDocuments} documents
            </p>
          </div>
        )}
      </div>

      <DeleteDocumentModal
        document={documentToDelete}
        isDeleting={isDeleting}
        onConfirmDelete={confirmDelete}
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
      />

      <AddTagsToDocumentsModal
        isOpen={isAddTagsModalOpen}
        setIsOpen={setIsAddTagsModalOpen}
        documentIds={Array.from(selectedDocIds)}
        onComplete={() => {
          setSelectedDocIds(new Set());
          refetch();
          refreshSidebar();
        }}
      />

      <ShareDocumentModal
        isOpen={isShareModalOpen}
        setIsOpen={setIsShareModalOpen}
        document={documentToShare}
        members={members}
        onShare={handleShare}
      />

      <NoAccessModal
        isOpen={isNoAccessModalOpen}
        onClose={() => setIsNoAccessModalOpen(false)}
        documentName={noAccessDocument?.name}
        ownerEmail={
          noAccessDocument ? getOwnerEmail(noAccessDocument) : undefined
        }
        ownerName={
          noAccessDocument ? getOwnerName(noAccessDocument) : undefined
        }
        requesterName={currentUserMember?.name || user?.email || undefined}
        requesterEmail={user?.email || undefined}
        autoSendRequest={autoSendAccessRequest}
      />
    </>
  );
};

export default Documents;
